import { Box, Spinner } from '@chakra-ui/react'
import { useProductQuery } from '@stocker/codegen/vendure'
import { ProductPageResponsive } from '@stocker/ui-components/custom'
import { Seo } from '@stocker/ui-components/design-system'
import { getServerData } from '@stocker/ui-components/helpers'
import { QueryClient, dehydrate } from '@tanstack/react-query'
import type { GetStaticPaths, GetStaticProps, InferGetStaticPropsType, NextPage } from 'next'
import { useRouter } from 'next/router'
import type { ParsedUrlQuery } from 'querystring'

interface IStaticParams extends ParsedUrlQuery {
  slug: string
}

export const getStaticPaths: GetStaticPaths<IStaticParams> = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps = async (context) => {
  const { slug } = context.params as IStaticParams
  const queryClient = new QueryClient()

  try {
    const data = await useProductQuery.fetcher({
      slug,
      withCustomFields: true,
      isPreRender: true,
    })()

    if (!data.product) {
      console.log(`[Product detail page] Product not found for slug: ${slug}`)

      return { notFound: true }
    }

    queryClient.setQueryData(
      useProductQuery.getKey({
        slug,
        withCustomFields: true,
        isPreRender: true,
      }),
      data,
    )
  } catch (error) {
    // return handlePrefetchError(error)
    console.log(`[Product detail page] Error fetching product: ${error}`)

    return {
      notFound: true,
      revalidate: 1,
    }
  }

  const serverData = await getServerData()

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      revalidateTime: Date.now(),
      ...serverData, // Data needed for _app.tsx Header, Footer etc
    },
    revalidate: 600,
  }
}

const ProductPage: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = () => {
  const router = useRouter()
  const { slug } = router.query
  const { data, error, isLoading } = useProductQuery(
    { slug: slug as string, withCustomFields: true, isPreRender: true },
    { staleTime: 1000 * 60 * 10 },
  )

  // TODO: [AC-357] Actual loading state
  if (isLoading) {
    return <Spinner />
  }

  // TODO: Actual error state
  if (error) {
    return <Box>An Error has Occured...</Box>
  }

  if (data?.product) {
    return (
      <>
        {/* here we filter out the _ from the name that used for the red formatting */}
        <Seo title={(data.product.customFields?.productDetailName ?? data.product.name).replaceAll('_', '')} />
        <ProductPageResponsive product={data.product} />
      </>
    )
  }

  return <Box>Something went wrong...</Box>
}

export default ProductPage
